import { NestedTask, TaskLinkType, TaskStatus, Memoize, UnallocatedUser, GeneralSetting, TaskGroupBy, LabelType, isStampedTaskCompleted } from '@jms/model';
import { DateTime } from 'luxon';
import { closedTaskStatusTag } from './task.closed.status';

export interface TaskEvidenceIcon {
    icon: string;
    exists: boolean;
    className?: string;
}

export interface TaskTag {
    name: string;
    type: TaskLinkType | string;
    color: string;
}

export class NestedTaskInfo {

    private dateFormat = 'dd/MM/yyyy';

    constructor(
        public readonly task: NestedTask,
        private setting: GeneralSetting,
        private timezone: string = 'utc'
    ) {
        if (this.task?.dueDate) {
            const date = DateTime.fromJSDate(new Date(this.task?.dueDate), { zone: this.timezone })
            this.isDueDate = date < DateTime.now().setZone(this.timezone);
        }

        if (this.setting?.region?.dateFormat) {
            this.dateFormat = this.setting?.region?.dateFormat;
        }
    }

    get id(): string {
        return this.task._id;
    }

    get name(): string {
        return this.task.name;
    }

    get type(): TaskLinkType {
        return this.task.type;
    }

    get hasDueDate() { return !!this.task?.dueDate }

    get dueDate(): string {
        return DateTime.fromJSDate(new Date(this.task?.dueDate), { zone: this.timezone }).toFormat(this.dateFormat);
    }

    public isDueDate: boolean;

    @Memoize()
    get assigneeName(): string {
        return !!this?.task?.assignee?.name
            ? this?.task?.assignee?.name
            : UnallocatedUser?._id
    }

    @Memoize()
    get supportsEvidence(): boolean {
        return this.task.type === TaskLinkType.Compliance;
    }

    @Memoize()
    get isGroup(): boolean {
        return [TaskLinkType.Group, TaskLinkType.WorkOrder, TaskLinkType.Job, TaskLinkType.Opportunity, TaskLinkType.Phase].includes(this.task.type);
    }

    @Memoize()
    get canAssign(): boolean {
        return ![TaskLinkType.Group, TaskLinkType.Phase, TaskLinkType.WorkOrder, TaskLinkType.Job].includes(this.task.type)
            && this.task.parent?.type !== TaskLinkType.Checklist;
    }

    @Memoize()
    get canViewDetails(): boolean {
        return this.canAssign;
    }

    /** Drives the strike-through; Not Required and Cannot Complete also mirror legacy Completed but were not done (WBS-13176). */
    @Memoize()
    get isCompleted(): boolean {
        return isStampedTaskCompleted(this.task);
    }

    @Memoize()
    get closedStatus() {
        return closedTaskStatusTag(this.task);
    }

    @Memoize()
    get expandable(): boolean {
        return this.isGroup || this.task.children?.length > 0 || (this.task.daily && !this.task.instance);
    }

    @Memoize()
    get stats(): string {
        const total = this.task?.children?.length ?? 0;
        const completed = this.task?.children?.filter(x => x.status === TaskStatus.Completed).length ?? 0;
        return `${completed}/${total}`;
    }

    @Memoize()
    get tags(): TaskTag[] {

        const task = this.task;
        const tags: TaskTag[] = [];

        if (!task) {
            return tags;
        }

        if (task.tags?.length) {
            if (task.currentGroupBy !== TaskGroupBy.Tags) {
                tags.push(...task.tags.filter(e => e.type === LabelType.File).map(e => {
                    return {
                        name: e.name,
                        type: e.type,
                        color: e.color
                    } as TaskTag
                }));
            }

            if (task.currentGroupBy !== TaskGroupBy.Phase) {
                tags.push(...task.tags.filter(e => e.type === LabelType.Phase).map(e => {
                    return {
                        name: e.name,
                        type: e.type,
                        color: e.color
                    } as TaskTag
                }));
            }

        }

        if (task.currentGroupBy !== TaskGroupBy.Type) {
            if ([TaskLinkType.Compliance, TaskLinkType.Item].includes(task.type)) {
                tags.push({
                    name: task.type,
                    type: task.type,
                } as TaskTag);
            }
        }

        if (task.target && task.currentGroupBy !== TaskGroupBy.Target) {
            tags.push({
                name: task.target,
            } as TaskTag);
        }

        return tags;
    }

    @Memoize()
    get systemGenerated(): boolean {
        return this.task.systemGenerated;
    }

    @Memoize()
    get evidenceIcons(): TaskEvidenceIcon[] {

        const icons: TaskEvidenceIcon[] = [];

        const task = this.task;

        if (task?.mandatory || task?.mandatoryForClaiming) {
            icons.push({
                icon: 'fa-asterisk',
                exists: true,
                className: 'mandatory'
            });
        }

        if (task.signatureEnabled) {
            icons.push({
                icon: 'fa-signature',
                exists: !!task.signature || !!task.signatureAttachments?.length
            });
        }

        if (task.beforePhotoEnabled || task.duringPhotoEnabled || task.afterPhotoEnabled) {
            icons.push({
                icon: 'fa-image',
                exists: !!task.beforePhotos?.length || !!task.duringPhotos?.length || !!task.afterPhotos?.length
            });
        }

        if (task.fileEnabled) {
            icons.push({
                icon: 'fa-file',
                exists: !!task.fileResponse?.length
            });
        }

        if (task.formEnabled) {
            icons.push({
                icon: 'fa-clipboard-list',
                exists: !!task.formResponseId?.length || !!task.formAttachments?.length,
                className: !!task.formResponseId?.length && !task.formCompleted ? 'partial-form' : null,
            });
        }

        if (task.additionalFiles?.length) {
            icons.push({
                icon: 'fa-paperclip',
                exists: true
            });
        }


        if (task.commentEnabled) {
            icons.push({
                icon: 'fa-comment',
                exists: !!task.comment?.length
            });
        }


        return icons;
    }

    @Memoize()
    get validationIcon(): TaskEvidenceIcon | null {

        const validation = this.task.validation;
        if (!validation) return null;

        switch (validation.status) {
            case 'pending':
                return { icon: 'fa-spinner-third fa-spin', exists: true, className: 'validation-pending' };
            case 'passed':
                return { icon: 'fa-circle-check', exists: true, className: 'validation-passed' };
            case 'failed':
                return { icon: 'fa-circle-exclamation', exists: true, className: 'validation-failed' };
            case 'warning':
                return { icon: 'fa-triangle-exclamation', exists: true, className: 'validation-warning' };
            case 'approved':
                return { icon: 'fa-circle-check', exists: true, className: 'validation-approved' };
            case 'declined':
                return { icon: 'fa-circle-xmark', exists: true, className: 'validation-declined' };
            default:
                return null;
        }
    }

    @Memoize()
    get validationTooltip(): string {

        switch (this.task?.validation?.status) {
            case 'pending': return 'Checking submission...';
            case 'passed': return 'Submission verified';
            case 'failed': return 'Submission needs review';
            case 'warning': return 'Minor issues found';
            case 'approved': return 'Submission approved';
            case 'declined': return 'Returned for re-submission';
            default: return '';
        }
    }

    @Memoize()
    public get isDailyParent(): boolean {
        return this.task?.daily && !this.task?.instance;
    }

    @Memoize()
    public get isItemChild(): boolean {
        return this.task?.parent?.type === TaskLinkType.Item || (this.task.daily && this.task.instance);
    }

    @Memoize()
    public get isDaily(): boolean {
        return this.task?.daily;
    }

}
